/**
 * ABOUTME: Utility functions for SmartQuote component
 * ABOUTME: Handles protocol generation, form validation, and platform detection
 */
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';

// Form validation for contact info (simplified - only requires name)
export function validateContactForm(
  name: string
): { isValid: boolean; errorMessage: string | null } {
  if (!name.trim()) {
    return { isValid: false, errorMessage: 'Please enter your name' };
  }

  return { isValid: true, errorMessage: null };
}

// Generate message template with user details
export function generateMessageTemplate(
  text: string,
  postcode: string | undefined,
  name: string
): string {
  return `Hi there,

I'm looking for a quote for the following work${postcode ? ` in ${postcode.toUpperCase()}` : ''}:

${text || '[Please describe your job here]'}

Could you let me know your availability and provide a rough quote for this job?

Thank you,
${name || '[Your name]'}`;
}

// Update template with actual user name
export function updateMessageWithName(message: string, name: string): string {
  return message.replace(/Thank you,\n\[Your name\]|Thank you,\n.*$/, `Thank you,\n${name}`);
}

// Generate mailto URL for email sending
export function generateMailtoUrl(
  selectedTradespeople: Tradesperson[],
  selectedRecipients: Record<string, boolean>,
  subject: string = 'Quote Request',
  message: string
): string {
  const selectedEmails = selectedTradespeople
    .filter(trade => trade.email && selectedRecipients[trade.id])
    .map(trade => trade.email);
  
  if (selectedEmails.length === 0) {
    return '';
  }
  
  const body = encodeURIComponent(message);
  
  // Use BCC for privacy between recipients
  return `mailto:?bcc=${selectedEmails.join(',')}&subject=${encodeURIComponent(subject)}&body=${body}`;
}

// Generate SMS link for text messaging
export function generateSmsLink(phoneNumber: string, message: string): string {
  if (!phoneNumber) return '';
  
  const body = encodeURIComponent(message);
  
  // Use different formats based on platform
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return `sms:${phoneNumber}&body=${body}`;
  } else {
    return `sms:${phoneNumber}?body=${body}`;
  }
}

// Detect mobile platform
export function isMobilePlatform(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

// Detect Safari browser
export function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

// Detect mobile Safari specifically
export function isMobileSafari(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as Window & { MSStream?: unknown }).MSStream &&
    /Safari/.test(navigator.userAgent);
}

// Open URL via direct DOM approach
export function openUrl(url: string): boolean {
  try {
    // Create and trigger a link element directly
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    
    // Add to DOM, click, then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    debug.error('Error opening URL:', error);
    return false;
  }
}